<template>
  <div>
    <TablePagination
      :paginateStart="paginateStart"
      :items="items"
      :totalItems="totalItems"
    />
    <div class="table-responsive mb-0">
      <b-table
        :items="items"
        :fields="fields"
        responsive="sm"
        :per-page="0"
        :current-page="currentPage"
        bordered
        hover
        v-on="clickable ? { [`row-clicked`]: onRowClicked } : {}"
        :tbody-tr-class="rowClass"
      >
        <!-- A custom formatted created_at -->
        <template v-slot:cell(created_at)="{ item }">
          <b class="text-info">{{ convertDate(item.created) }}</b>
        </template>

        <template v-slot:cell(created)="{ item }">
          <span>{{ convertDate(item.created) }}</span>
        </template>

        <!-- A custom formatted actual_receiving_date-->
        <template v-slot:cell(actual_receiving_date)="{ item }">
          <span>{{ convertDate(item.actual_receiving_date) }}</span>
        </template>

        <!-- A custom formatted product_types -->
        <template v-slot:cell(skus)="{ item }">
          <span>{{ item.products.length }}</span>
        </template>

        <!-- A custom formatted product_delivery_speed -->
        <template v-slot:cell(delivery-speed)="{ item }">
          <toggle-button
            :width="100"
            :value="item.delivery_days.id === 1 ? true : false"
            :labels="{ checked: 'Next Day', unchecked: 'Same Day' }"
            @change="
              handleChangeDeliverySpeed({
                id: item.id,
                delivery_day_id: item.delivery_days.id === 1 ? 2 : 1,
              })
            "
          />
        </template>
        <!-- A custom formatted product_channel -->
        <template v-slot:cell(marketplace)="{ item }">
          <v-img
            class="mx-auto"
            :src="detectChannel(item.marketplace)"
            height="35"
            contain
            width="80"
          />
        </template>
        <!-- A custom formatted status -->
        <template v-slot:cell(status)="{ item }">
          <div class="d-flex align-center justify-center">
            <span
              class="badge font-size-13"
              :class="detectStatus(item.status)"
              >{{ item.status }}</span
            >
            <ToolTip
              v-if="item.status_comment"
              :content="item.status_comment"
            />
          </div>
        </template>
        <template v-slot:cell(merchantStatus)="{ item }">
          <div class="d-flex justify-content-center align-center">
            <span
              v-if="item.status == true"
              class="delivered-badge text-white rounded px-2 py-1"
              >Active</span
            >
            <span v-else class="canceled-badge text-white rounded px-2 py-1"
              >Inactive</span
            >
          </div>
        </template>

        <template v-slot:cell(drafted)="{ item }">
          <div class="d-flex justify-content-center align-center">
            <span
              v-if="item.drafted == 0"
              class="delivered-badge text-white rounded px-2 py-1"
              >Live</span
            >
            <span v-else class="on-hold-badge text-white rounded px-2 py-1"
              >Booked</span
            >
          </div>
        </template>
        <!-- A custom formatted for tokens permissions -->
        <template v-slot:cell(permissions)="{ item }">
          <ul class="permissions-wrapper">
            <li v-for="permission in item.permissions" :key="permission">
              {{ permission }}
            </li>
          </ul>
        </template>
        <!-- A custom formatted actions -->
        <template v-slot:cell(actions)="{ item }">
          <router-link
            :to="{
              name: showPage,
              params: { id: item.id, rowData: item },
            }"
          >
            <b-btn class="bg-dark border-0 text--white"
              ><span>{{ $t("Show") }}</span></b-btn
            >
          </router-link>
        </template>
        <!-- A custom formatted edit -->
        <template v-slot:cell(edit)="{ item }">
          <router-link
            :to="{
              name: showPage,
              params: { id: item.id, rowData: item },
            }"
          >
            <b-btn class="bg-dark border-0 text--white"
              ><span>{{ $t("Edit") }}</span></b-btn
            >
          </router-link>
        </template>

        <!-- A custom Status Switch -->
        <template v-slot:cell(switch)="{ item }">
          <div class="d-flex justify-content-center">
            <toggle-button
              :width="105"
              :height="30"
              :font-size="13"
              :color="{ checked: '#43A047', unchecked: '#ea352b' }"
              :labels="{ checked: 'Activate', unchecked: 'Deactivate' }"
              :value="Boolean(item.status)"
              @change="handleChangeBan(item.id, $event)"
            />
          </div>
        </template>
        <!-- A custom formatted actions for revoke tokens -->
        <template v-slot:cell(revoke)="{ item }">
          <b-btn
            class="bg-danger border-0 text--white"
            @click="revokeToken(item.id)"
            ><span>Revoke</span></b-btn
          >
        </template>

        <!-- A custom formatted Attachments -->
        <template v-slot:cell(attachments)="{ item }">
          <div v-if="item.has_attachments" class="attachments d-flex">
            <button
              :class="
                isRtl
                  ? 'border-0 rounded-2 px-1 ml-1 w-50 fw-medium text-no-wrap'
                  : 'border-0 rounded-2 px-1 mr-1 w-50 fw-medium text-no-wrap'
              "
              @click="downloadFile(item.id, 'invoice')"
            >
              <span>{{ $t("Invoice") }}</span>
              <v-icon class="mx-1" size="15">$vuetify.icons.export</v-icon>
            </button>

            <button
              class="border-0 rounded-2 px-1 w-75 fw-medium text-no-wrap"
              @click="downloadFile(item.id, 'awb')"
            >
              <span>{{ $t("AWB") }}</span>
              <v-icon class="ml-1" size="15">$vuetify.icons.export</v-icon>
            </button>
          </div>
          <div v-else>No Attachments</div>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import { channels } from "@/mixins/channels";
import TablePagination from "../table-pagination.vue";
import ToolTip from "./toolTip.vue";

export default {
  mixins: [channels],
  props: {
    items: {
      type: Array,
      default: function () {
        return [];
      },
      required: true,
    },
    totalItems: {
      type: Number,
      required: true,
      default: 0,
    },
    fields: {
      type: Array,
      default: function () {
        return [];
      },
      required: true,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    clickable: Boolean,
    showPage: String,
    paginateStart: Number,
    handleChangeDeliverySpeed: Function,
    revokeToken: Function,
    handleChangeBan: Function,
    onRowClicked: Function,
    downloadFile: Function,
  },
  components: { TablePagination, ToolTip },
  data() {
    return {
      show: false,
    };
  },
  mounted() {},
  methods: {
    rowClass(item, type) {
      if (!item || type !== "row") return;
      if (item.status === "On Hold") return "on-hold-style";
    },
  },
};
</script>
<style lang="scss">
.permission-td {
  width: 40%;
  .permissions-wrapper {
    margin: 0;
    column-count: 2;
    li {
      margin-bottom: 5px;
    }
  }
}

.table-bordered {
  background-color: #fff;
  th,
  td {
    text-align: center;
    background-color: #fff;
  }

  th {
    color: #101820;
    white-space: nowrap;
  }
}
.attachments {
  button {
    height: 35px;
    span {
      color: #57697b;
    }
    background: #c6c9cc;
  }
}

@media screen and (max-width: 992px) {
  .table-bordered {
    th,
    td {
      white-space: nowrap;
    }
  }
}
.on-hold-style {
  animation: animatedRoutes 5s ease-in-out reverse forwards infinite;
}
@keyframes animatedRoutes {
  0% {
    background-color: #f7e9d4;
  }
  25% {
    background-color: var(--v-white-base);
  }
  50% {
    background-color: #f7e9d4;
  }
  75% {
    background-color: var(--v-white-base);
  }
  100% {
    background-color: #f7e9d4;
  }
}
</style>
